/** Consolidation results block for Entity Structure (QOE-RECON-1 —
 *  extracted from EntityStructurePage.tsx to clear the FILE_SIZE_POLICY.md
 *  400-line threshold). Pure presentation: the run-consolidation API call
 *  and its state stay in the parent page; this only renders `result`. */
import { CheckCircle, AlertTriangle } from 'lucide-react'
import type { ConsolidationResult, PreliminaryStamp, DealEntity } from '@/api/client'
import PreliminaryStampBanner from './PreliminaryStampBanner'
import ReconciliationPanel from './ReconciliationPanel'
import { methodLabel, fmtMoneyOrDash } from './shared'

export default function ConsolidationResults({
  result,
  stamp,
  dealId,
  entities,
}: {
  result: ConsolidationResult
  stamp?: PreliminaryStamp
  dealId: string
  entities: DealEntity[]
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-emerald-700">
        <CheckCircle className="w-4 h-4" />
        <span className="font-medium text-sm">Consolidation complete</span>
      </div>

      {/* CONSOL-FIX-1 P0 — repeated immediately above the figures. "Complete"
          above must never be read as "issuable". */}
      <PreliminaryStampBanner stamp={result.preliminary_stamp ?? stamp} />


      {/* FIELDS-1 P2 / CONSOL-FIX-1 C-C — deal-perimeter caveat. Emitted by
          build_qoe_finding_from_eliminations only when at least one entity
          is carved out. The engine NO LONGER nets these out: a transaction
          with a retained affiliate survives into the figures below and is
          disclosed under ASC 850 instead, because the buyer keeps paying it
          after close. */}
      {typeof result.qoe_finding?.carved_out_warning === 'string' && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Deal perimeter</p>
            <p className="mt-0.5">{String(result.qoe_finding.carved_out_warning)}</p>
            <p className="mt-1">
              Transactions with a retained entity are kept in the figures below
              and disclosed as related-party, not eliminated. Confirm each one is
              priced at market before the number is relied on.
            </p>
          </div>
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wide">
          {/* C-E — "Consolidated" vs "Combined" is a real distinction, not a
              synonym. Every period in a run carries the same label. */}
          {result.consolidated_periods[0]?.statement_label ?? 'Consolidated'} Financials
          by Period
        </div>
        {/* Wide table: scroll rather than clip on a narrow viewport. */}
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-600">Period</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">Revenue</th>
              {/* QOE-RECON-1 Order step 4 — cogs/gross_profit/operating_expenses/
                  net_income were computed by run_consolidation and returned to the
                  client since entities.py, but never rendered here. */}
              <th className="text-right px-4 py-2 font-medium text-gray-600">COGS</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">Gross Profit</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">Opex</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">EBITDA</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">NCI Adj.</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">Adj. EBITDA (Consol.)</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">Attrib. to Parent</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">SDE</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600">Net Income</th>
              <th className="text-center px-4 py-2 font-medium text-gray-600">IC Elim.</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {result.consolidated_periods.map(p => (
              <tr key={p.period_label} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 font-medium">{p.period_label}</td>
                <td className="px-4 py-2.5 text-right">${p.revenue.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right">{fmtMoneyOrDash(p.cogs)}</td>
                <td className="px-4 py-2.5 text-right">{fmtMoneyOrDash(p.gross_profit)}</td>
                <td className="px-4 py-2.5 text-right">{fmtMoneyOrDash(p.operating_expenses)}</td>
                <td className="px-4 py-2.5 text-right">${p.ebitda.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right text-orange-600">
                  {p.nci_adjustment !== 0
                    ? `($${Math.abs(p.nci_adjustment).toLocaleString()})`
                    : '—'}
                </td>
                <td className="px-4 py-2.5 text-right font-semibold">
                  ${p.adjusted_ebitda_consolidated.toLocaleString()}
                </td>
                {/* C-B — full consolidation takes 100% of every line, so
                    the minority owners' share has to come back out. This
                    column is what actually belongs to the buyer. */}
                <td className="px-4 py-2.5 text-right">
                  {p.ebitda_attributable_to_parent !== undefined
                    ? `$${p.ebitda_attributable_to_parent.toLocaleString()}`
                    : '—'}
                </td>
                {/* DECISION-1 — owner benefit defaults to SDE. */}
                <td className="px-4 py-2.5 text-right">
                  {p.sde_consolidated !== undefined
                    ? `$${p.sde_consolidated.toLocaleString()}`
                    : '—'}
                </td>
                <td className="px-4 py-2.5 text-right">{fmtMoneyOrDash(p.net_income)}</td>
                <td className="px-4 py-2.5 text-center">
                  {p.elimination_count > 0 ? (
                    <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                      {p.elimination_count} auto
                    </span>
                  ) : '—'}
                  {p.review_count > 0 && (
                    <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                      {p.review_count} review
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* NCI note */}
      {result.consolidated_periods.some(p => p.nci_adjustment !== 0) && (
        <p className="text-xs text-gray-500">
          * NCI (Non-Controlling Interest) is disclosed below consolidated EBITDA per QoE convention.
        </p>
      )}

      {/* CONSOL-FIX-1 C-A/C-B — how each entity was classified, and why.
          This is what the CPA signs off against; a consolidated number
          whose method cannot be explained is not reviewable. */}
      {result.consolidated_periods[0]?.method_rationale &&
        result.consolidated_periods[0].method_rationale!.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wide">
            Consolidation Method by Entity (ASC 810)
          </div>
          <div className="divide-y text-xs">
            {result.consolidated_periods[0].method_rationale!.map((m, i) => (
              <div key={i} className="px-4 py-2">
                <span className="font-medium text-gray-800">
                  {String(m.entity_name ?? '')}
                </span>
                <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                  {methodLabel(String(m.method ?? ''))}
                </span>
                <p className="mt-0.5 text-gray-600">{String(m.rationale ?? '')}</p>
                {m.citation ? (
                  <p className="mt-0.5 text-gray-400">{String(m.citation)}</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONSOL-FIX-1 C-C — ASC 850 disclosures. These are NOT eliminated:
          a retained affiliate keeps charging the business after close, so
          the cost is real to the buyer. Common-control leases additionally
          need normalizing to market rent (ASU 2018-17). */}
      {result.consolidated_periods.some(
        p => (p.related_party_disclosures?.length ?? 0) > 0,
      ) && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs font-medium text-amber-700 uppercase tracking-wide">
            Related-Party Transactions — Retained Entities (ASC 850)
          </div>
          <div className="divide-y text-xs">
            {result.consolidated_periods.flatMap(p =>
              (p.related_party_disclosures ?? []).map((d, i) => (
                <div key={`${p.period_label}-${i}`} className="px-4 py-2">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-gray-700">{d.description}</span>
                      <span className="ml-2 text-gray-400">
                        {d.entity_a} ↔ {d.entity_b}
                      </span>
                    </div>
                    <span className="text-gray-500 whitespace-nowrap">
                      ${d.amount.toLocaleString()} · {d.period}
                    </span>
                  </div>
                  {d.note && <p className="mt-0.5 text-amber-700">{d.note}</p>}
                </div>
              )),
            )}
          </div>
        </div>
      )}

      {/* QOE-RECON-1 — screen-vs-consolidation tie-out, replaces the old
          "Intercompany Eliminations Detail" block above. Per-period
          contribution-by-entity → eliminations → excluded → consolidated
          tie, built to _ClaudeCode_Briefs/QOE-RECON-1_approved_screen_mock.html. */}
      <ReconciliationPanel dealId={dealId} result={result} entities={entities} />

      {/* Warnings */}
      {result.consolidated_periods.some(p => p.warnings.length > 0) && (
        <div className="space-y-1">
          {result.consolidated_periods.flatMap(p => p.warnings).map((w, i) => (
            <div
              key={i}
              className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700"
            >
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {w}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
